import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { DeleteButtonWithConfirmation } from 'components/Common/Button/DeleteButtonWithConfirmation';
import { MoneyInput } from 'components/Common/Input/MoneyInput';
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon';
import { useAppController } from 'customHooks/useAppController';
import { bomItemQuantitySelector } from 'features/BillOfMaterials/store/selectors/bomItemQuantitySelector';
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector';
import { projectIdSelector } from 'features/BillOfMaterials/store/selectors/projectIdSelector';
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors';
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc';
import {
    IncomingRfqsController,
    incomingRfqRequestIds,
} from 'features/RequestForQuotations/RFQ/IncomingRfqsController';
import { outgoingQuotationRequestIds } from 'features/RequestForQuotations/Quotation/OutgoingQuotationsController';
import { isEqual } from 'lodash';
import { SumMoney } from 'model/Money';
import {
    AssemblyHeaderRow,
    BomItemType,
    PartTypeRow,
} from 'model/Project/BoMItemRow';
import { BomItemPointer } from 'model/Project/BomItemPointer';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    MoneyDto,
    QuantityDto,
    QuantityDtoType,
    QuotationBoMItemActivityLineDto,
    QuotationBoMItemLineDto,
    QuotationDto,
    QuotationLineDto,
     IncomingRequestForQuotationDto,
     OutgoingQuotationState,
} from 'services/APIs/InternalAPI/internal-api.contracts';
import store, { RootAppState, useAppSelector } from 'store/configureStore';
import { useShowException } from 'utils/useShowException';
import { ProjectQuotationsController } from '../ProjectQuotationController';
import {
    DEFAULT_QUOTATION_VALIDITY_DAYS,
    getSynchronizedQuotationDates,
    QuotationDetailsRef,
    QuotationDetailsSection,
} from './QuotationDetailsSection';

const outgoingQuotationColorMap: Record<
  OutgoingQuotationState,
  {
    color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning'
    variant: 'filled' | 'outlined'
  }
> = {
  [OutgoingQuotationState.Draft]: { color: 'warning', variant: 'filled' },
  [OutgoingQuotationState.Sent]: { color: 'info', variant: 'filled' },
  [OutgoingQuotationState.AcceptedByBuyer]: {
    color: 'success',
    variant: 'filled',
  },
  [OutgoingQuotationState.RejectedByBuyer]: {
    color: 'error',
    variant: 'filled',
  },
  [OutgoingQuotationState.Cancelled]: { color: 'error', variant: 'filled' },
}

export type ManageOutgoingQuotationDialogProps = {
  isOpen: boolean
  onClose: () => void
  onQuotationSent?: () => void
}

type RfqBomItem = NonNullable<
  IncomingRequestForQuotationDto['boMItemReferences']
>[number]

type LocalResponsePriceMap = Record<string, MoneyDto>
type LocalLineCommentMap = Record<string, string>
type LocalQuotationLineMap = Record<string, QuotationLineDto>
type LocalActivitySelectionMap = Record<string, boolean>
type LocalManualPriceMap = Record<string, true>

function getRfqItemLookupKeys(item: RfqBomItem): string[] {
  return [item.sellerBoMItemId].filter((value): value is string => !!value)
}

function getValueForRfqItem<T>(
  item: RfqBomItem,
  valueMap: Record<string, T>,
): T | undefined {
  for (const key of getRfqItemLookupKeys(item)) {
    const value = valueMap[key]
    if (value !== undefined) {
      return value
    }
  }

  return undefined
}

function getActivitySelectionKey(
  item: RfqBomItem,
  activity: QuotationBoMItemActivityLineDto,
  index: number,
): string {
  const itemKey = item.sellerBoMItemId || 'unmapped-item'
  return `${itemKey}:${
    activity.boMItemActivityId ||
    activity.originalActivityId ||
    activity.workingStepType ||
    index
  }`
}

function getActivityBatchSelectionKey(
  activity: QuotationBoMItemActivityLineDto,
): string {
  return (
    activity.workingStepType ||
    activity.originalActivityId ||
    activity.boMItemActivityId
  )
}

function getDefaultActivitySelection(
  activity: QuotationBoMItemActivityLineDto,
): boolean {
  return activity.includeInQuotation ?? true
}

function getActivitySalesPrice(
  activity: QuotationBoMItemActivityLineDto,
): MoneyDto | null {
  return toMoneyDto(activity.salesPrice)
}

function getSelectedActivitySalesPrice(
  item: RfqBomItem,
  activitySelection: LocalActivitySelectionMap,
  quotationLine?: QuotationLineDto,
): MoneyDto | null {
  return sumMoneyValues(
    getQuotationLineActivities(quotationLine).map((activity, index) => {
      const isSelected =
        activitySelection[getActivitySelectionKey(item, activity, index)] ??
        getDefaultActivitySelection(activity)

      return isSelected ? getActivitySalesPrice(activity) : null
    }),
  )
}

function formatMoney(money: MoneyDto | null | undefined): string {
  if (!money?.value && money?.value !== 0) {
    return '-'
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`.trim()
  }
}

function toMoneyDto(money: MoneyDto | null | undefined): MoneyDto | null {
  if (!money) {
    return null
  }

  if ('amount' in money) {
    return {
      value: money.amount as number,
      currency: money.currency,
      symbol: money.symbol,
    }
  }

  if ('value' in money) {
    return money
  }

  return null
}

function sumMoneyValues(
  moneyValues: Array<MoneyDto | null | undefined>,
): MoneyDto | null {
  const normalized = moneyValues
    .map(toMoneyDto)
    .filter((money): money is MoneyDto => money?.value !== undefined)

  if (normalized.length === 0) {
    return null
  }

  return SumMoney(normalized)
}

function getQuotationLineActivities(
  line: QuotationLineDto | undefined,
): QuotationBoMItemActivityLineDto[] {
  return (line?.quotationBomItemLines ?? []).flatMap(
    (bomItemLine) => bomItemLine.activities ?? [],
  )
}

function getQuotationLineSalesPrice(line: QuotationLineDto): MoneyDto | null {
  if (hasMoneyValue(line.salesPrice)) {
    return line.salesPrice
  }

  return sumMoneyValues(
    getQuotationLineActivities(line).map((activity) => activity.salesPrice),
  )
}

function createZeroMoney(currency?: string): MoneyDto {
  return {
    value: 0,
    currency,
  }
}

function createZeroRatio(): QuantityDto {
  return {
    value: 0,
    unit: undefined,
    quantityType: QuantityDtoType.Ratio,
  }
}

function hasMoneyValue(money: MoneyDto | null | undefined): boolean {
  return money?.value !== undefined && money.value !== null
}

function getBomItemType(
  state: RootAppState,
  bomItemId: string,
): BomItemType | null {
  if (!bomItemId) {
    return null
  }

  if (state.project.partTypes?.[bomItemId]) {
    return BomItemType.partType
  }

  if (state.project.partInstances?.[bomItemId]) {
    return BomItemType.partInstance
  }

  if (state.project.assemblyHeaders?.[bomItemId]) {
    return BomItemType.assemblyType
  }

  if (state.project.assemblyInstances?.[bomItemId]) {
    return BomItemType.assemblyInstance
  }

  return null
}

const OutgoingQuotationItemRow = memo(
  function OutgoingQuotationItemRow({
    rfqItem,
    quotationLine,
    responsePrice,
    suggestedResponsePrice,
    activitySelection,
    lineComment,
    onActivitySelectionChange,
    onActivityBatchSelectionChange,
    onResponsePriceChange,
    onLineCommentChange,
    disabled,
  }: {
    rfqItem: RfqBomItem
    quotationLine: QuotationLineDto | undefined
    responsePrice: MoneyDto | null
    suggestedResponsePrice: MoneyDto | null
    activitySelection: LocalActivitySelectionMap
    lineComment: string
    onActivitySelectionChange: (
      bomItemId: string,
      activityKey: string,
      selected: boolean,
    ) => void
    onActivityBatchSelectionChange: (
      activityBatchKey: string,
      selected: boolean,
    ) => void
    onResponsePriceChange: (bomItemId: string, price: MoneyDto | null) => void
    onLineCommentChange: (bomItemId: string, comment: string) => void
    disabled?: boolean
  }) {
    const bomItemType = useAppSelector((state: RootAppState) =>
      getBomItemType(state, rfqItem.sellerBoMItemId),
    )

    const bomItemPointer = useMemo<BomItemPointer | null>(() => {
      if (!bomItemType) {
        return null
      }

      return {
        id: rfqItem.sellerBoMItemId,
        type: bomItemType,
      }
    }, [bomItemType, rfqItem.sellerBoMItemId])

    const bomItem = useAppSelector((state: RootAppState) => {
      if (!bomItemPointer) {
        return null
      }

      return bomItemSelector(state, bomItemPointer)
    }, isEqual) as AssemblyHeaderRow | PartTypeRow | null

    const bomItemQuantity = useAppSelector((state: RootAppState) => {
      if (!bomItemPointer) {
        return null
      }

      return bomItemQuantitySelector(bomItemPointer)(state)
    }, isEqual)

    const thumbnailSrc = useBomItemThumbnailImageSrc(
      bomItemPointer || { id: '', type: BomItemType.partType },
    )

    const itemName =
      bomItem?.name || bomItem?.originalFileName || rfqItem.buyerOriginalName

    const handlePriceChange = useCallback(
      (event: { currentTarget: { value: MoneyDto | null } }) => {
        onResponsePriceChange(
          rfqItem.sellerBoMItemId,
          event.currentTarget.value,
        )
      },
      [rfqItem.sellerBoMItemId, onResponsePriceChange],
    )

    const handleCommentChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onLineCommentChange(rfqItem.sellerBoMItemId, e.target.value)
      },
      [rfqItem.sellerBoMItemId, onLineCommentChange],
    )

    const activityClickTimeoutRef = useRef<number | null>(null)

    useEffect(() => {
      return () => {
        if (activityClickTimeoutRef.current) {
          window.clearTimeout(activityClickTimeoutRef.current)
        }
      }
    }, [])

    const quotationActivities = getQuotationLineActivities(quotationLine)

    return (
      <TableRow
        sx={{
          borderLeft: (theme) =>
            bomItemPointer
              ? `3px solid transparent`
              : `3px solid ${theme.palette.warning.light}`,
          '&:last-child td': { borderBottom: 0 },
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.selected,
          },
        }}
      >
        <TableCell sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {thumbnailSrc.imageSrc ? (
              <Box
                component="img"
                src={thumbnailSrc.imageSrc}
                alt={itemName}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.action.hover,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.disabled',
                  fontSize: 10,
                  flexShrink: 0,
                }}
              >
                N/A
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                }}
              >
                {itemName}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  fontSize: '0.65rem',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 240,
                }}
              >
                {'Buyer BoM item ID: ' + rfqItem.buyerBoMItemId}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  fontSize: '0.65rem',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 240,
                }}
              >
                {'proj bomItemId: ' + (bomItemPointer?.id ?? 'N/A')}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {quotationActivities.length > 0 ? (
              quotationActivities.map((activity, index) => {
                const activityKey = getActivitySelectionKey(
                  rfqItem,
                  activity,
                  index,
                )
                const isSelected =
                  activitySelection[activityKey] ??
                  getDefaultActivitySelection(activity)
                const activityBatchKey = getActivityBatchSelectionKey(activity)
                const activityPrice = getActivitySalesPrice(activity)

                return (
                  <Box
                    key={activityKey}
                    component="button"
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (activityClickTimeoutRef.current) {
                        window.clearTimeout(activityClickTimeoutRef.current)
                      }

                      activityClickTimeoutRef.current = window.setTimeout(
                        () => {
                          onActivitySelectionChange(
                            rfqItem.sellerBoMItemId,
                            activityKey,
                            !isSelected,
                          )
                          activityClickTimeoutRef.current = null
                        },
                        200,
                      )
                    }}
                    onDoubleClick={(event) => {
                      event.preventDefault()

                      if (activityClickTimeoutRef.current) {
                        window.clearTimeout(activityClickTimeoutRef.current)
                        activityClickTimeoutRef.current = null
                      }

                      onActivityBatchSelectionChange(
                        activityBatchKey,
                        !isSelected,
                      )
                    }}
                    title={
                      isSelected
                         ? 'Remove from quotation'
                         : 'Add to quotation'
                    }
                    sx={{
                      appearance: 'none',
                      border: (t) =>
                        isSelected
                          ? `2px solid ${t.palette.info.main}`
                          : 'none',
                      borderRadius: 1,
                      backgroundColor: isSelected
                        ? 'action.hover'
                        : 'transparent',
                      // color: isSelected
                      //   ? 'info.contrastText'
                      //   : 'text.secondary',
                      cursor: 'pointer',
                      '&:disabled': {
                        cursor: 'default',
                        opacity: 0.7,
                      },
                      minWidth: 58,
                      px: 1,
                      py: 0.75,
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <WorkingStepIconSvg
                      workingStepType={activity.workingStepType}
                      attributes={{ width: '32px' }}
                    />
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: 'inherit', lineHeight: 1.1 }}
                    >
                      {formatMoney(activityPrice)}
                    </Typography>
                  </Box>
                )
              })
            ) : (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {bomItemQuantity ?? '-'}
          </Typography>
        </TableCell>
        <TableCell
          align="right"
          sx={{
            minWidth: 180,
            pt: 1.25,
            verticalAlign: 'center',
          }}
        >
          {bomItemPointer ? (
            <Box
              sx={{
                maxWidth: 180,
                ml: 'auto',
                transform: 'translateY(8px)',
              }}
            >
              <MoneyInput
                defaultMoneyObject={responsePrice}
                onChange={handlePriceChange}
                value={responsePrice}
                allowNegative={false}
                disabled={disabled}
              />
              {suggestedResponsePrice ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Suggested {formatMoney(suggestedResponsePrice)}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Typography>-</Typography>
          )}
        </TableCell>
        <TableCell sx={{ minWidth: 240, py: 1.25, verticalAlign: 'center' }}>
          <TextField
            variant="standard"
            placeholder="line notes"
            value={lineComment}
            onChange={handleCommentChange}
            disabled={disabled}
            fullWidth
            multiline
            maxRows={3}
            onFocus={(e) => e.target.select()}
            slotProps={{
              input: {
                sx: {
                  fontSize: (theme) => theme.typography.body2.fontSize,
                },
              },
              htmlInput: {
                maxLength: 150,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.8rem',
                backgroundColor: 'background.paper',
              },
            }}
          />
        </TableCell>
      </TableRow>
    )
  },
  (prevProps, nextProps) =>
    prevProps.rfqItem.sellerBoMItemId === nextProps.rfqItem.sellerBoMItemId &&
    isEqual(prevProps.quotationLine, nextProps.quotationLine) &&
    isEqual(prevProps.responsePrice, nextProps.responsePrice) &&
    isEqual(
      prevProps.suggestedResponsePrice,
      nextProps.suggestedResponsePrice,
    ) &&
    isEqual(prevProps.activitySelection, nextProps.activitySelection) &&
    prevProps.lineComment === nextProps.lineComment &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onActivitySelectionChange ===
      nextProps.onActivitySelectionChange &&
    prevProps.onActivityBatchSelectionChange ===
      nextProps.onActivityBatchSelectionChange &&
    prevProps.onResponsePriceChange === nextProps.onResponsePriceChange &&
    prevProps.onLineCommentChange === nextProps.onLineCommentChange,
)

export const ManageOutgoingQuotationDialog = ({
  isOpen,
  onClose,
  onQuotationSent,
}: ManageOutgoingQuotationDialogProps) => {
  const projectId = useAppSelector(projectIdSelector)
  const rfqMetadata = useAppSelector(
    (state) => state.project.activeProject?.rfqMetadata,
  )

  const { controller: rfqController, loading: rfqLoading } = useAppController(
    () => new IncomingRfqsController(),
  )
  const { controller: quotationController, loading: quotationLoading } =
    useAppController(() => new ProjectQuotationsController())

  const ShowException = useShowException('quotation')
  const [rfq, setRfq] = useState<IncomingRequestForQuotationDto | null>(null)
  const [localResponsePrices, setLocalResponsePrices] =
    useState<LocalResponsePriceMap>({})
  const [localLineComments, setLocalLineComments] =
    useState<LocalLineCommentMap>({})
  const [localActivitySelection, setLocalActivitySelection] =
    useState<LocalActivitySelectionMap>({})
  const [manuallyEditedPriceItemIds, setManuallyEditedPriceItemIds] =
    useState<LocalManualPriceMap>({})
  const [detailsExpanded, setDetailsExpanded] = useState(true)

  const detailsRef = useRef<QuotationDetailsRef>(null)

  const handleDialogContentScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const shouldExpandDetails = event.currentTarget.scrollTop === 0
      setDetailsExpanded((current) =>
        current === shouldExpandDetails ? current : shouldExpandDetails,
      )
    },
    [],
  )

  useEffect(() => {
    if (!isOpen || !rfqMetadata?.createdFromRequestForQuotationId) {
      return undefined
    }

    let isMounted = true

    const fetchRfq = async () => {
      try {
        const response = await rfqController.detail(
          rfqMetadata.createdFromRequestForQuotationId,
        )

        if (isMounted) {
          setRfq(response)
        }
      } catch (error) {
        ShowException(error)
      }
    }

    fetchRfq()

    return () => {
      isMounted = false
    }
  }, [
    isOpen,
    rfqController,
    rfqMetadata?.createdFromRequestForQuotationId,
    ShowException,
  ])

  const [outgoingQuotation, setOutgoingQuotation] =
    useState<QuotationDto | null>(null)
  const [outgoingQuotationId, setOutgoingQuotationId] = useState<string>()
  const [outgoingQuotationState, setOutgoingQuotationState] =
    useState<OutgoingQuotationState>()
  const hasNotifiedQuotationSentRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      hasNotifiedQuotationSentRef.current = false
      setDetailsExpanded(true)
    }
  }, [isOpen])

  const isCreating =
    quotationLoading[outgoingQuotationRequestIds.draft(projectId)]

  const isSaving = outgoingQuotation
    ? quotationLoading[outgoingQuotationRequestIds.save(outgoingQuotation.id)]
    : false

  const isSending = outgoingQuotation
    ? quotationLoading[outgoingQuotationRequestIds.send(outgoingQuotation.id)]
    : false

  const isLoadingOutgoingQuotation =
    quotationLoading[outgoingQuotationRequestIds.list] ||
    quotationLoading[outgoingQuotationRequestIds.draft(projectId)]

  const sendDisabled =
    !projectId ||
    !rfqMetadata?.forwardId ||
    !rfqMetadata?.forwardSender ||
    isCreating

  const isOutgoingQuotationReadOnly =
    outgoingQuotationState !== undefined &&
    outgoingQuotationState !== OutgoingQuotationState.Draft
  const canSendOutgoingQuotation = !isOutgoingQuotationReadOnly

  const rfqItems = useMemo(() => rfq?.boMItemReferences ?? [], [rfq])
  const serverQuotationLines = useMemo(() => {
    const linesByBomItemId: LocalQuotationLineMap = {}

    for (const line of outgoingQuotation?.lines ?? []) {
      linesByBomItemId[line.id] = line
    }

    return linesByBomItemId
  }, [outgoingQuotation])

  const projectOrderNumber = useAppSelector(
    projectSelectors.projectOrderNumberSelector,
  )

  const projectReference = useAppSelector(
    projectSelectors.projectReferenceSelector,
  )
  const commentFromProject = useAppSelector(
    (state) => state.project.activeProject?.comment,
  )
  const expireDateFromProject = useAppSelector(
    (state) => state.project.activeProject?.quotationExpiresAt,
  )
  const validityDaysFromProject = useAppSelector(
    (state) => state.project.activeProject?.quotationExpiresIn,
  )

  const projectDetailsRef = useRef({
    projectOrderNumber,
    projectReference,
    commentFromProject,
    expireDateFromProject,
    validityDaysFromProject,
  })
  projectDetailsRef.current = {
    projectOrderNumber,
    projectReference,
    commentFromProject,
    expireDateFromProject,
    validityDaysFromProject,
  }

  const fetchQuotation = useCallback(async () => {
    try {
      const response = await quotationController.getOutgoingQuotation()
      const q = response.quotation

      setOutgoingQuotation(q ?? null)
      setOutgoingQuotationId(response.id)
      setOutgoingQuotationState(response.state)

      if (
        response.state === OutgoingQuotationState.Sent &&
        !hasNotifiedQuotationSentRef.current
      ) {
        hasNotifiedQuotationSentRef.current = true
        onQuotationSent?.()
      }

      if (q) {
        const synchronizedDates = getSynchronizedQuotationDates({
          validityDays: q.validityPeriodInDays,
          expiryDate: q.quotationExpireDate,
        })

        detailsRef.current?.setValues({
          comment: q.comment ?? '',
          reference: q.reference ?? '',
          orderNumber: q.orderNumber ?? '',
          deliveryTermsCode: q.deliveryTermsCode ?? '',
          paymentTermsCode: q.paymentTermsCode ?? '',
          vatRatio: q.vatRatio,
          expireDate: synchronizedDates.expiryDate,
          validityDays: synchronizedDates.validityDays,
        })
      }
    } catch (err) {
      ShowException(err)
    }
  }, [quotationController, onQuotationSent, ShowException])

  const refreshRfq = useCallback(async () => {
    if (!rfqMetadata?.createdFromRequestForQuotationId) {
      return
    }

    try {
      const response = await rfqController.detail(
        rfqMetadata.createdFromRequestForQuotationId,
      )

      setRfq(response)
    } catch (error) {
      ShowException(error)
    }
  }, [
    rfqController,
    rfqMetadata?.createdFromRequestForQuotationId,
    ShowException,
  ])

  const refreshDialogData = useCallback(async () => {
    await Promise.all([fetchQuotation(), refreshRfq()])
  }, [fetchQuotation, refreshRfq])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    fetchQuotation()
  }, [isOpen, fetchQuotation])

  // Initialize prices and comments when both the quotation and RFQ items are available.
  useEffect(() => {
    if (!isOpen || rfqItems.length === 0) {
      return
    }

    const fetchedComments: LocalLineCommentMap = {}

    for (const line of outgoingQuotation?.lines ?? []) {
      if (line.comment) {
        fetchedComments[line.id] = line.comment
      }
    }

    setLocalResponsePrices((previous) => {
      const initialPrices: LocalResponsePriceMap = {}

      for (const item of outgoingQuotation?.lines ?? []) {
        const salesPrice = getQuotationLineSalesPrice(item)

        if (salesPrice) {
          initialPrices[item.id] = salesPrice
        }
      }

      return isEqual(previous, initialPrices) ? previous : initialPrices
    })

    setLocalLineComments((previous) => {
      const initialComments: LocalLineCommentMap = {}

      for (const item of outgoingQuotation?.lines ?? []) {
        initialComments[item.id] = item.comment
        continue
      }

      return isEqual(previous, initialComments) ? previous : initialComments
    })

    setLocalActivitySelection((previous) => {
      const initialSelection: LocalActivitySelectionMap = {}

      for (const item of rfqItems) {
        const quotationLine = getValueForRfqItem(item, serverQuotationLines)

        for (const [index, activity] of getQuotationLineActivities(
          quotationLine,
        ).entries()) {
          initialSelection[getActivitySelectionKey(item, activity, index)] =
            getDefaultActivitySelection(activity)
        }
      }

      return isEqual(previous, initialSelection) ? previous : initialSelection
    })
  }, [isOpen, rfqItems, outgoingQuotation, serverQuotationLines])

  const handleLocalPriceChange = useCallback(
    (bomItemId: string, price: MoneyDto | null) => {
      setManuallyEditedPriceItemIds((previous) => ({
        ...previous,
        [bomItemId]: true,
      }))
      setLocalResponsePrices((previous) => {
        if (!price) {
          const rest = { ...previous }
          delete rest[bomItemId]
          return rest
        }

        return {
          ...previous,
          [bomItemId]: price,
        }
      })
    },
    [],
  )

  const handleActivitySelectionChange = useCallback(
    (bomItemId: string, activityKey: string, selected: boolean) => {
      const rfqItem = rfqItems.find(
        (item) => item.sellerBoMItemId === bomItemId,
      )
      const quotationLine = rfqItem
        ? getValueForRfqItem(rfqItem, serverQuotationLines)
        : undefined

      setLocalActivitySelection((previous) => {
        const nextSelection = {
          ...previous,
          [activityKey]: selected,
        }

        if (rfqItem && !manuallyEditedPriceItemIds[bomItemId]) {
          const suggestedPrice = getSelectedActivitySalesPrice(
            rfqItem,
            nextSelection,
            quotationLine,
          )

          setLocalResponsePrices((previousPrices) => {
            if (!suggestedPrice) {
              const rest = { ...previousPrices }
              delete rest[bomItemId]
              return rest
            }

            return {
              ...previousPrices,
              [bomItemId]: suggestedPrice,
            }
          })
        }

        return nextSelection
      })
    },
    [manuallyEditedPriceItemIds, rfqItems, serverQuotationLines],
  )

  const handleActivityBatchSelectionChange = useCallback(
    (activityBatchKey: string, selected: boolean) => {
      setLocalActivitySelection((previous) => {
        const nextSelection = { ...previous }
        const affectedBomItemIds = new Set<string>()

        for (const item of rfqItems) {
          const quotationLine = getValueForRfqItem(item, serverQuotationLines)

          for (const [index, activity] of getQuotationLineActivities(
            quotationLine,
          ).entries()) {
            if (getActivityBatchSelectionKey(activity) !== activityBatchKey) {
              continue
            }

            nextSelection[getActivitySelectionKey(item, activity, index)] =
              selected
            if (item.sellerBoMItemId) {
              affectedBomItemIds.add(item.sellerBoMItemId)
            }
          }
        }

        setLocalResponsePrices((previousPrices) => {
          let nextPrices = previousPrices

          for (const item of rfqItems) {
            if (
              !item.sellerBoMItemId ||
              !affectedBomItemIds.has(item.sellerBoMItemId) ||
              manuallyEditedPriceItemIds[item.sellerBoMItemId]
            ) {
              continue
            }

            const suggestedPrice = getSelectedActivitySalesPrice(
              item,
              nextSelection,
              getValueForRfqItem(item, serverQuotationLines),
            )

            if (!suggestedPrice) {
              if (nextPrices[item.sellerBoMItemId] === undefined) {
                continue
              }

              const rest = { ...nextPrices }
              delete rest[item.sellerBoMItemId]
              nextPrices = rest
              continue
            }

            if (isEqual(nextPrices[item.sellerBoMItemId], suggestedPrice)) {
              continue
            }

            nextPrices = {
              ...nextPrices,
              [item.sellerBoMItemId]: suggestedPrice,
            }
          }

          return nextPrices
        })

        return isEqual(previous, nextSelection) ? previous : nextSelection
      })
    },
    [manuallyEditedPriceItemIds, rfqItems, serverQuotationLines],
  )

  const handleLineCommentChange = useCallback(
    (bomItemId: string, comment: string) => {
      setLocalLineComments((previous) => ({
        ...previous,
        [bomItemId]: comment,
      }))
    },
    [],
  )

  const handleSave = async (): Promise<string | undefined> => {
    if (!outgoingQuotation) {
      return undefined
    }

    const details = detailsRef.current?.getValues()
    const quotationLines: Array<QuotationLineDto> = []
    const existingLinesById = Object.fromEntries(
      (outgoingQuotation.lines ?? []).map((line) => [line.id, line]),
    )

    for (const item of rfqItems) {
      const localPrice = getValueForRfqItem(item, localResponsePrices)

      const bomItem = bomItemSelector(store.getState(), {
        id: item.sellerBoMItemId,
        type: undefined,
      }) as AssemblyHeaderRow | PartTypeRow | null

      if (!bomItem) {
        continue
      }

      const existingLine = getValueForRfqItem(item, existingLinesById)
      const selectedActivityPrice = getSelectedActivitySalesPrice(
        item,
        localActivitySelection,
        existingLine,
      )
      const responsePrice =
        localPrice ??
        selectedActivityPrice ??
        createZeroMoney(outgoingQuotation.currencyCode)
      const existingBomItemLine = existingLine?.quotationBomItemLines?.[0]
      const quotationBomItemActivities = getQuotationLineActivities(
        existingLine,
      ).map((activity, index): QuotationBoMItemActivityLineDto => {
        const selected =
          localActivitySelection[
            getActivitySelectionKey(item, activity, index)
          ] ?? getDefaultActivitySelection(activity)

        return {
          ...activity,
          salesPrice:
            activity.salesPrice ?? createZeroMoney(responsePrice.currency),
          discountValue:
            activity.discountValue ?? createZeroMoney(responsePrice.currency),
          surchargeValue:
            activity.surchargeValue ?? createZeroMoney(responsePrice.currency),
          discountRatio: activity.discountRatio ?? createZeroRatio(),
          surchargeRatio: activity.surchargeRatio ?? createZeroRatio(),
          includeInQuotation: selected,
        }
      })
      const quotationBomItemLines: QuotationBoMItemLineDto[] = [
        {
          ...existingBomItemLine,
          boMItemId: existingBomItemLine?.boMItemId ?? bomItem.id,
          name: existingBomItemLine?.name ?? bomItem.name,
          originalName: existingBomItemLine?.originalName ?? bomItem.name,
          quantity:
            existingBomItemLine?.quantity ??
            bomItem.financial.totalProjectQuantity,
          comment: existingBomItemLine?.comment ?? bomItem.comment,
          thickness: existingBomItemLine?.thickness ?? {
            value: 0,
            unit: undefined,
            quantityType: QuantityDtoType.Length,
          },
          activities: quotationBomItemActivities,
        },
      ]

      quotationLines.push({
        ...existingLine,
        id: bomItem.id,
        comment:
          getValueForRfqItem(item, localLineComments) ||
          bomItem.comment ||
           'Price provided in quotation',
        salesPrice: responsePrice,
        name: bomItem.name,
        quantity: bomItem.financial.totalProjectQuantity,
        discountValue:
          existingLine?.discountValue ??
          createZeroMoney(responsePrice.currency),
        surchargeValue:
          existingLine?.surchargeValue ??
          createZeroMoney(responsePrice.currency),
        discountRatio: existingLine?.discountRatio ?? createZeroRatio(),
        surchargeRatio: existingLine?.surchargeRatio ?? createZeroRatio(),
        quotationBomItemLines,
      })
    }

    const validityPeriodInDays =
      details?.validityDays ?? DEFAULT_QUOTATION_VALIDITY_DAYS

    const quotationDto: QuotationDto = {
      ...outgoingQuotation,
      comment: details?.comment || undefined,
      reference: details?.reference || undefined,
      orderNumber: details?.orderNumber || undefined,
      deliveryTermsCode: details?.deliveryTermsCode || undefined,
      paymentTermsCode: details?.paymentTermsCode || undefined,
      vatRatio: details?.vatRatio ?? outgoingQuotation.vatRatio,
      quotationExpireDate: details?.expireDate
        ? new Date(`${details.expireDate}T00:00:00`)
        : outgoingQuotation.quotationExpireDate,
      validityPeriodInDays,
      lines: quotationLines,
    }

    try {
      const response = await quotationController.saveOutgoingQuotation({
        outgoingQuotationId: outgoingQuotationId ?? outgoingQuotation.id,
        quotationDto,
      })

      await refreshDialogData()
      return response.outgoingQuotationId
    } catch (error) {
      ShowException(error)
      return undefined
    }
  }

  const handleSend = async () => {
    const quotationId = await handleSave()
    if (!quotationId) {
      return
    }

    try {
      await quotationController.sendOutgoingQuotation(quotationId)
      await refreshDialogData()
    } catch (err) {
      ShowException(err)
    }
  }

  const handleDelete = async (quotationId: string) => {
    try {
      await quotationController.deleteOutgoingQuotation(quotationId)
      setOutgoingQuotation(null)
      setOutgoingQuotationId(undefined)
      setOutgoingQuotationState(undefined)
      setLocalResponsePrices({})
      setLocalLineComments({})
      setLocalActivitySelection({})
      setManuallyEditedPriceItemIds({})
    } catch (err) {
      ShowException(err)
    }
  }

  const pricedItemsCount = useMemo(
    () =>
      rfqItems.filter(
        (item) =>
          getValueForRfqItem(item, localResponsePrices)?.value !== undefined,
      ).length,
    [localResponsePrices, rfqItems],
  )

  const totalResponsePrice = useMemo(() => {
    const prices = rfqItems
      .map((item) => getValueForRfqItem(item, localResponsePrices))
      .filter((p): p is MoneyDto => p?.value !== undefined)

    if (prices.length === 0) return null

    return SumMoney(prices)
  }, [localResponsePrices, rfqItems])

  const suggestedTotalResponsePrice = useMemo(() => {
    const prices = rfqItems
      .map((item) =>
        getSelectedActivitySalesPrice(
          item,
          localActivitySelection,
          getValueForRfqItem(item, serverQuotationLines),
        ),
      )
      .filter((p): p is MoneyDto => p?.value !== undefined)

    if (prices.length === 0) return null

    return SumMoney(prices)
  }, [localActivitySelection, rfqItems, serverQuotationLines])

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: -0.2 }}
            >
              Outgoing quotation
            </Typography>
            {outgoingQuotation && (
              <Chip
                label={outgoingQuotationState ?? OutgoingQuotationState.Draft}
                color={
                  outgoingQuotationColorMap[
                    outgoingQuotationState ?? OutgoingQuotationState.Draft
                  ]?.color ?? 'default'
                }
                variant={
                  outgoingQuotationColorMap[
                    outgoingQuotationState ?? OutgoingQuotationState.Draft
                  ]?.variant ?? 'outlined'
                }
                size="small"
              />
            )}
          </Box>
        </Box>
        {outgoingQuotation && (
          <DeleteButtonWithConfirmation
            color="error"
            buttonType="button"
            onDelete={() =>
              handleDelete(outgoingQuotationId ?? outgoingQuotation.id)
            }
            muiButtonProps={{
              size: 'small',
               disabled: sendDisabled || isOutgoingQuotationReadOnly,
              startIcon: quotationLoading[
                outgoingQuotationRequestIds.delete(
                  outgoingQuotationId ?? outgoingQuotation.id,
                )
              ] ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined,
            }}
          />
        )}
      </DialogTitle>

      <Divider />

      <DialogContent
          data-testid="outgoing-quotation-scroll-container"
        onScroll={handleDialogContentScroll}
        sx={{ p: 0, backgroundColor: 'background.default' }}
      >
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          {!rfqMetadata?.forwardId || !rfqMetadata?.forwardSender ? (
            <Alert severity="warning" variant="outlined">
              Missing RFQ routing metadata. Cannot send from this project.
            </Alert>
          ) : null}
        </Box>

        <QuotationDetailsSection
          ref={detailsRef}
           disabled={isOutgoingQuotationReadOnly}
          expanded={detailsExpanded}
          onExpandedChange={setDetailsExpanded}
        />

        <Divider />

        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Line Items
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Set quotation prices and notes
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Chip
                label={`${rfqItems.length} items`}
                size="small"
                variant="outlined"
                sx={{ backgroundColor: 'background.paper' }}
              />
              <Chip
                label={`${pricedItemsCount} priced`}
                size="small"
                color={pricedItemsCount > 0 ? 'success' : 'default'}
                variant="outlined"
                sx={{ backgroundColor: 'background.paper' }}
              />
              {totalResponsePrice && (
                <Chip
                  label={`Total: ${formatMoney(totalResponsePrice)}`}
                  size="medium"
                  color="primary"
                  variant="outlined"
                  sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}
                />
              )}
              {suggestedTotalResponsePrice && (
                <Chip
                  label={`Suggested: ${formatMoney(suggestedTotalResponsePrice)}`}
                  size="medium"
                  color="info"
                  variant="outlined"
                  sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'background.paper',
            }}
          >
            Quotation prices are local and do not update BOM financials.
          </Alert>

          {rfqLoading[
                 incomingRfqRequestIds.detail(
                   rfqMetadata?.createdFromRequestForQuotationId ?? '',
                 )
          ] || isLoadingOutgoingQuotation ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rfqItems.length > 0 ? (
            <TableContainer
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.shadows[1],
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        backgroundColor: (theme) => theme.palette.grey[50],
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Item
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Activities
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                      Qty
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                      Response price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Comment
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfqItems.map((item, index) => {
                    const quotationLine = getValueForRfqItem(
                      item,
                      serverQuotationLines,
                    )

                    return (
                      <OutgoingQuotationItemRow
                        key={`${item.sellerBoMItemId ?? item.buyerBoMItemId}-${index}`}
                        rfqItem={item}
                        quotationLine={quotationLine}
                        responsePrice={
                          getValueForRfqItem(item, localResponsePrices) || null
                        }
                        suggestedResponsePrice={getSelectedActivitySalesPrice(
                          item,
                          localActivitySelection,
                          quotationLine,
                        )}
                        activitySelection={localActivitySelection}
                        lineComment={
                          getValueForRfqItem(item, localLineComments) ?? ''
                        }
                        onActivitySelectionChange={
                          handleActivitySelectionChange
                        }
                        onActivityBatchSelectionChange={
                          handleActivityBatchSelectionChange
                        }
                        onResponsePriceChange={handleLocalPriceChange}
                        onLineCommentChange={handleLineCommentChange}
                         disabled={isOutgoingQuotationReadOnly}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="warning" variant="outlined">
              No RFQ items found for this project.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <Divider />

      {/* <JsonViewer data={localResponsePrices} /> */}

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          gap: 1,
          backgroundColor: 'background.paper',
          boxShadow: (theme) => `0 -8px 20px ${theme.palette.action.hover}`,
        }}
      >
        <Box sx={{ mr: 'auto', color: 'text.secondary' }}>
          <Typography variant="caption">
            {pricedItemsCount} of {rfqItems.length} lines ready to save
          </Typography>
        </Box>
        <Button onClick={onClose} size="small">
          Close
        </Button>
        {/* {!outgoingQuotation && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={sendDisabled}
            size="small"
            startIcon={
              isCreating ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
             Create quotation
          </Button>
        )} */}
        {outgoingQuotation && !isOutgoingQuotationReadOnly && (
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={!!isSaving}
            size="small"
            startIcon={
              isSaving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )
            }
          >
            Save
          </Button>
        )}
        {outgoingQuotation && canSendOutgoingQuotation && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sendDisabled || !!isSending || !!isSaving}
            size="small"
            startIcon={
              isSending || isSaving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
          >
             Send quotation to buyer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
